import React, { useContext, useEffect, useState } from "react";
import * as yup from "yup";
import { SubstacaoComponentForm } from "./SubstacaoComponentForm";
import { useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid } from "@mui/material";
import PrimaryBaseButton from "../../../main/components/PrimaryBaseButton";
import { PostUG, PostUsina } from "../types";
import { postUgsToUgsData, ugsDataToPostUGs } from "../ugsMapper";
import { UGsFormContent } from "./UGsFormContent";
import { UsinasContext } from "../context/UsinasContextProvider";
import AddIcon from "@mui/icons-material/Add";

const producerSchema = yup.object().shape({
    producerContactPhone: yup.string().optional().nullable(),
    producerContactEmail: yup.string().optional().nullable(),
    producerPortalAccessLink: yup.string().optional().nullable(),
    producerPortalAccessUsername: yup.string().optional().nullable(),
    producerPortalAccessPassword: yup.string().optional().nullable(),
});

const ugSchema = yup.object().shape({
    number: yup.number().optional().nullable(),
    hasTransformer: yup.boolean().optional().nullable(),
    hasSmartLogger: yup.boolean().optional().nullable(),
    disjuntorTag: yup.string().optional().nullable(),
    invertersAmount: yup
        .number()
        .optional()
        .nullable()
        .transform((value) => (isNaN(value) ? 0 : value)),
    inverterPower: yup
        .number()
        .optional()
        .nullable()
        .transform((value) => (isNaN(value) ? 0 : value)),
    invertersProducers: yup.array().of(producerSchema).optional().nullable(),
    trackersAmount: yup
        .number()
        .optional()
        .nullable()
        .transform((value) => (isNaN(value) ? 0 : value)),
    trackersProducers: yup.array().of(producerSchema).optional().nullable(),
    modulesAmount: yup
        .number()
        .optional()
        .nullable()
        .transform((value) => (isNaN(value) ? 0 : value)),
    modulesPower: yup
        .number()
        .optional()
        .nullable()
        .transform((value) => (isNaN(value) ? 0 : value)),
    modulesProducers: yup.array().of(producerSchema).optional().nullable(),
    stringAmount: yup
        .number()
        .optional()
        .nullable()
        .transform((value) => (isNaN(value) ? 0 : value)),
    stringPower: yup
        .number()
        .optional()
        .nullable()
        .transform((value) => (isNaN(value) ? 0 : value)),
});
const subestacaoSchema = yup.object().shape({
    hasSubstation: yup.boolean().optional().nullable(),
    hasTransformer: yup.boolean().optional().nullable(),
    disjuntorTag: yup.string().optional().nullable(),
});
const schema = yup.object().shape({
    substation: subestacaoSchema,
    ugs: yup.array().of(ugSchema).optional(),
});
export type UGsData = yup.InferType<typeof schema>;
export type UGData = yup.InferType<typeof ugSchema>;
export type ProducersData = yup.InferType<typeof producerSchema>;

const UFVForm = React.forwardRef(
    (
        props: {
            selectedStep: number;
            setSelectedStep: (step: number) => void;
            onSubmitFromUFV?: (data: UGsData) => void;
        },
        ref
    ) => {
        const {
            control,
            formState: { errors },
            handleSubmit,
            setValue,
        } = useForm({
            resolver: yupResolver(schema),
        });

        const {
            fields: ugsFields,
            append: appendUG,
            remove: removeUG,
        } = useFieldArray({
            control,
            name: "ugs",
        });

        const { newUsina, setNewUsina } = useContext(UsinasContext);

        function updateData() {
            const data = postUgsToUgsData(newUsina?.uGs);
            if (!data) {
                setValue("ugs", [
                    {
                        number: undefined,
                        invertersProducers: [{}],
                        trackersProducers: [{}],
                        modulesProducers: [{}],
                    },
                ]);
                return;
            }
            setValue("ugs", data);
        }
        useEffect(() => {
            updateData();
            setValue("substation", {
                hasSubstation: newUsina?.substation?.hasSubstation ?? false,
                hasTransformer: newUsina?.substation?.hasTransformer ?? false,
                disjuntorTag: newUsina?.substation?.disjuntorTag ?? undefined,
            });
        }, []);

        const onSubmit = (data: UGsData) => {
            if (props.onSubmitFromUFV) {
                props.onSubmitFromUFV(data);
                return;
            }
            const ugs: PostUG[] | undefined = ugsDataToPostUGs(data.ugs);
            const newUsinaData: PostUsina = {
                ...newUsina,
                uGs: ugs,
                substation: {
                    hasSubstation: data.substation?.hasSubstation ?? false,
                    hasTransformer: data.substation?.hasTransformer ?? false,
                    disjuntorTag: data.substation?.disjuntorTag ?? undefined,
                    commands: [],
                },
            };
            setNewUsina(newUsinaData);
            props.setSelectedStep(props.selectedStep + 1);
        };

        React.useImperativeHandle(ref, () => ({
            submitForm() {
                handleSubmit(onSubmit)();
            },
        }));

        const [openUGs, setOpenUGs] = useState<string[]>([]);

        return (
            <Grid
                container
                component={"form"}
                width={"100%"}
                alignItems="center"
                onSubmit={handleSubmit(onSubmit)}
                spacing={2}
                // sx={{
                //   display: "flex",
                //   justifyContent: "center",
                // }}
                paddingX={"10px"}
            >
                <SubstacaoComponentForm control={control} errors={errors} setValue={setValue} />
                {ugsFields.map((field, index) => (
                    <UGsFormContent
                        key={field.id}
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        index={index}
                        removeUG={removeUG}
                        openUGs={openUGs}
                        setOpenUGs={setOpenUGs}
                    />
                ))}
                <Grid item xs={12}>
                    <PrimaryBaseButton
                        onClick={() => {
                            appendUG({ number: 0 });
                        }}
                        title="Adicionar UG"
                        startIcon={<AddIcon />}
                        sx={{
                            width: "30%",
                            height: "38px",
                            marginLeft: "auto",
                            marginRight: "auto",
                        }}
                    />
                </Grid>
            </Grid>
        )
    }
);

export default UFVForm;